'use client';

import type { ButtonBaseProps } from '@mui/material/ButtonBase';
import { useState, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import ButtonBase from '@mui/material/ButtonBase';
import { Label } from '@/components/label';
import { Iconify } from '@/components/iconify';
import { CustomPopover, usePopover } from '@/components/custom-popover';
import { GQLQuery } from '@/lib/client';
import { Q_CLIENTS_T1 } from '@/lib/queries/client-t1.query';
import { Q_SESSION_SELF } from '@/lib/queries/session.query';
import { useWorkspace } from '@/contexts/workspace-context';

export type WorkspacesPopoverProps = ButtonBaseProps & {
  data?: {
    id: string;
    name: string;
    logo: string;
    plan: string;
  }[];
};

export function WorkspacesPopover({ sx, ...other }: WorkspacesPopoverProps) {
  const [data, setData] = useState<{
    id: string;
    name: string;
    logo: string;
    plan?: string;
  }[]
  >([])
  const usersQueryFilters = {}

  const { data: session } = GQLQuery({
    query: Q_SESSION_SELF,
    queryAction: 'sessionSelf',
    fetchPolicy: 'cache-first',
    pollInterval: 0,
  })
  const hasSessionTier1 = !!session?.user?.role?.clientTier1?.id
  const {
    data: clientsActive,
    loading: loadingCientsActive,
  } = GQLQuery({
    query: Q_CLIENTS_T1,
    queryAction: 'tier1Clients',
    variables: { input: usersQueryFilters },
    // Skip entirely when the user can't switch; otherwise fetch once and
    // serve from cache — this is a rarely-changing reference list, no need
    // to poll the endpoint on the default interval.
    skip: hasSessionTier1,
    pollInterval: 0,
    fetchPolicy: 'cache-first',
  })
  const popover = usePopover();
  const {
    workspace: activeWorkspace,
    setWorkspace: setActiveWorkspace,
    clearWorkspace,
  } = useWorkspace();

  const mediaQuery = 'sm';

  const [workspace, setWorkspace] = useState(data[0]);

  useEffect(() => {
    if (clientsActive?.rows) {
      const workspacesData = clientsActive.rows.map((client: any) => ({
        id: client.id,
        name: client.name,
        logo: `https://i.pravatar.cc/150?u=${client.id}`,
        // plan: 'Pro',
      }));
      setData(workspacesData);
    }
  }, [clientsActive])


  const handleChangeWorkspace = useCallback(
    (newValue: (typeof data)[0]) => {
      setWorkspace(newValue);
      setActiveWorkspace({ id: newValue.id, name: newValue.name, logo: newValue.logo })
      popover.onClose()
    },
    [popover, setActiveWorkspace],
  );

  useEffect(() => {
    if (hasSessionTier1 || loadingCientsActive) return;
    if (data.length === 0) {
      if (activeWorkspace) clearWorkspace();
      return;
    }

    const current = workspace?.id
      ? data.find((option) => option.id === workspace.id)
      : undefined;
    const persisted = activeWorkspace?.id
      ? data.find((d) => d.id === activeWorkspace.id)
      : undefined;
    const initial = current || persisted || data[0];

    if (workspace?.id !== initial.id) {
      setWorkspace(initial);
    }
    if (activeWorkspace?.id !== initial.id) {
      setActiveWorkspace({
        id: initial.id,
        name: initial.name,
        logo: initial.logo,
      });
    }
  }, [
    activeWorkspace,
    clearWorkspace,
    data,
    hasSessionTier1,
    loadingCientsActive,
    setActiveWorkspace,
    workspace?.id,
  ])

  // Nothing to switch between → render nothing. Avoids a confusing
  // empty/duplicate UI when the session is bound to a single Tier-1.
  if (hasSessionTier1 || data.length === 0) {
    return null;
  }

  return (
    <>
      <ButtonBase
        disableRipple
        onClick={popover.onOpen}
        sx={{
          py: 0.5,
          gap: { xs: 0.5, [mediaQuery]: 1 },
          ...sx,
        }}
        {...other}
      >
        <Box
          component="img"
          alt={workspace?.name}
          src={workspace?.logo}
          sx={{ width: 24, height: 24, borderRadius: '50%' }}
        />

        <Box
          component="span"
          sx={{
            typography: 'subtitle2',
            display: { xs: 'none', [mediaQuery]: 'inline-flex' },
          }}
        >
          {workspace?.name}
        </Box>

        { workspace?.plan && (
        <Label
          color={workspace?.plan === 'Free' ? 'default' : 'info'}
          sx={{
            height: 22,
            display: { xs: 'none', [mediaQuery]: 'inline-flex' },
          }}
        >
          {workspace?.plan}
        </Label>
        )}

        <Iconify width={16} icon="carbon:chevron-sort" sx={{ color: 'text.disabled' }} />
      </ButtonBase>

      <CustomPopover
        open={popover.open}
        anchorEl={popover.anchorEl}
        onClose={popover.onClose}
        slotProps={{ arrow: { placement: 'top-left' } }}
      >
        <MenuList sx={{ width: 240 }}>
          {data.map((option) => (
            <MenuItem
              key={option.id}
              selected={option.id === workspace?.id}
              onClick={() => handleChangeWorkspace(option)}
              sx={{ height: 48 }}
            >
              <Avatar alt={option.name} src={option.logo} sx={{ width: 24, height: 24 }} />

              <Box component="span" sx={{ flexGrow: 1 }}>
                {option.name}
              </Box>

              {option.plan && (
              <Label color={option.plan === 'Free' ? 'default' : 'info'}>{option.plan}</Label>
                )}
            </MenuItem>
          ))}
        </MenuList>
      </CustomPopover>
    </>
  );
}
